import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  isPushSupported,
  subscribeToPush as doSubscribeToPush,
  unsubscribeFromPush,
  getPushSubscriptions,
} from '../services/pushService';
import { logger } from '../lib/logger';

const log = logger.createNamespace('usePushNotifications');

export interface IncidentPushToast {
  title: string;
  body?: string;
  data?: Record<string, unknown>;
}

export interface UsePushNotificationsOptions {
  /** Called when a push notification indicates a new incident (refresh incident list) */
  onIncidentPush: () => void;
  /** Called when a push is received so the app can show an in-app toast */
  onShowToast?: (toast: IncidentPushToast) => void;
  /** Only auto-subscribe when user has completed onboarding */
  onboardingComplete?: boolean;
}

export interface UsePushNotificationsResult {
  isSupported: boolean;
  isSubscribed: boolean;
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<void>;
}

export function usePushNotifications({
  onIncidentPush,
  onShowToast,
  onboardingComplete = false,
}: UsePushNotificationsOptions): UsePushNotificationsResult {
  const { user } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    if (!isPushSupported() || !user) return;

    const checkSubscription = async () => {
      try {
        const list = await getPushSubscriptions();
        setIsSubscribed(list.length > 0);
      } catch {
        setIsSubscribed(false);
      }
    };

    checkSubscription();
  }, [user]);

  useEffect(() => {
    if (!user) {
      unsubscribeFromPush();
      setIsSubscribed(false);
      return;
    }
  }, [user]);

  useEffect(() => {
    if (!isPushSupported()) return;

    const handleMessage = (event: MessageEvent) => {
      const payload = event.data;
      if (payload?.type !== 'INCIDENT_PUSH_RECEIVED') return;

      const data = payload.data || {};
      const title = typeof data.title === 'string' ? data.title : 'New Incident';
      const body = typeof data.body === 'string' ? data.body : 'A new incident was reported in your zone';

      log.debug('Incident push received', { title, data });

      if (onShowToast) {
        onShowToast({ title, body, data });
      }
      onIncidentPush();
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, [onIncidentPush, onShowToast]);

  const subscribe = useCallback(async (): Promise<boolean> => {
    const result = await doSubscribeToPush();
    setIsSubscribed(result.ok);
    return result.ok;
  }, []);

  const unsubscribe = useCallback(async (): Promise<void> => {
    await unsubscribeFromPush();
    setIsSubscribed(false);
  }, []);

  return {
    isSupported: isPushSupported(),
    isSubscribed,
    subscribe,
    unsubscribe,
  };
}
